import * as _ from "lodash";

import { memoize, Nullable } from "@ctra/utils";

import { DataPointsBase } from "./DataPointsBase";

import { LineSource, LineData, ISODuration, ChartTimePeriod } from "../typings";
import { Series } from "../";
import { fillTimeSeriesGaps } from "../utils";

/**
 * Normalizer class for line chart data
 */
class LineNormalizer extends DataPointsBase {
  protected entity: LineSource;

  constructor(source: LineSource) {
    super(source);
    this.entity = source;
  }

  /**
   * Process the incoming line chart data
   */
  @memoize
  formatData({
    zeroRenderingInterval,
    timePeriod
  }: {
    zeroRenderingInterval: Nullable<ISODuration>;
    timePeriod: Nullable<ChartTimePeriod>;
  }): LineData["data"] {
    let dataSource = this.entity.data;

    if (zeroRenderingInterval && timePeriod) {
      dataSource = fillTimeSeriesGaps(
        this.entity.data,
        zeroRenderingInterval,
        timePeriod.startDate,
        timePeriod.endDate
      );
    }

    return _.reduce<NonNullable<LineSource["data"]>, NonNullable<LineData["data"]>>(
      dataSource,
      (acc, item, key) => {
        const series = Series.create(key, this.entity.meta.seriesType);
        const seriesField = series.getField();

        if (seriesField) {
          const list = _.map(item.points, ({ x, y, missing, context, anomaly, isolated }) => {
            const computedY = _.defaultTo(y, _.defaultTo(context?.projection, null));

            return {
              // @note format the x value in the chart renderer
              x,
              y: missing ? null : computedY,
              anomaly,
              isolated,
              seriesField: key,
              context
            };
          });

          return [...list, ...acc];
        }

        return acc;
      },
      []
    );
  }

  /**
   * Tell if the chart has an anomaly
   * @return {LineData["meta"]}
   */
  @memoize
  formatMetadata(): LineData["meta"] {
    const meta = DataPointsBase.prototype.formatMetadata.apply(this);
    const hasAnomaly = !!_.find(_.flatMap(this.entity.data, "points"), "anomaly");
    const hasIsolated = !!_.find(_.flatMap(this.entity.data, "points"), "isolated");
    const hasMissingData = !!_.find(_.flatMap(this.entity.data, "points"), (point) => !_.isNumber(point.y));

    return { ...meta, hasAnomaly, hasMissingData, hasIsolatedData: hasIsolated };
  }
}

export { LineNormalizer };
